import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationOptions } from "@tanstack/react-query";

import { noteKeys } from "@/features/notes/api/keyFactory";
import { showToast } from "@/providers/ToastProvider";
import type { UUID } from "@/core/types";

import { cosignatureKeys } from "../keyFactory";
import { rejectDocument, type RejectDocumentVariables } from "../queryFunctions/rejectDocument";

export function useRejectDocument({
  onSuccess,
  ...options
}: UseMutationOptions<void, unknown, RejectDocumentVariables> = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rejectDocument,
    ...options,
    onError: () => {
      showToast([{ heading: "Something went wrong. Please try again.", variant: "error" }]);
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: cosignatureKeys.base });
      await queryClient.invalidateQueries({
        queryKey: noteKeys.details(variables.documentId as UUID),
      });
      await queryClient.invalidateQueries({
        queryKey: noteKeys.summary(variables.patientId as UUID),
      });
      onSuccess?.(data, variables, context);
      showToast([
        { heading: "Note rejected with your feedback.", variant: "success" },
        { delay: 500 },
      ]);
    },
  });
}
